import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAnalytics } from '@app/hooks';
import { navigateToUrl } from '@app/services/windowService';
import { AlloyButton } from 'cat-ecommerce-alloy';
import { replaceTokensInUrl } from '@app/utils';
import { getUnitOfMeasureCookie } from '@app/components/common/UnitOfMeasureToggle/utils';
import { capitalizeText, replaceTokensInString } from '@app/utils/stringUtils';
import { PCC_JOBS_RECOMMENDATIONS_LIST } from '@app/constants/analyticsConstants';
import { LOCATION_HASH_KEY, UOM } from '@app/constants/commonConstants';
import links, { headlessLinks } from '@app/constants/links';
import styles from './JobRecommendations.module.scss';

const JobRecommendations = () => {
  const [t] = useTranslation();
  const { SSRO_BUNDLE_PAGE } = links;
  const { fireProductImpressionEvent, fireProductClickEvent } = useAnalytics();
  const defaultUnitOfMeasure = useSelector(s => s.common.defaultUnitOfMeasure);
  const catalogId = useSelector(s => s.common.catalogId);
  const langId = useSelector(s => s.common.langId);
  const storeId = useSelector(s => s.common.storeId);
  const salesModel = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const dealerStore = useSelector(s => s.common?.storeLocationName) ?? '';

  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const jobRecommandationData = useSelector(
    s => s.products?.jobsRecommendations
  );
  const esiteURL = useSelector(s => s.common.esiteURL);
  const isHeadlessBlocksSSOEnabled = useSelector(
    s => s.featureFlag.PCC_HeadlessBlocksSSO_Page
  );

  const getJobDetails = (item, index = 0) => ({
    businessUnit: 'PCC',
    id: item.jobId,
    list: PCC_JOBS_RECOMMENDATIONS_LIST,
    name: item.jobName,
    partNumber: item.jobId,
    position: index + 1,
    relatedModel: salesModel,
    kitId: item.jobId
  });

  const gaJobsData = jobRecommandationData?.map((item, index) =>
    getJobDetails(item, index)
  );

  const selectedUnit =
    getUnitOfMeasureCookie('uom') ??
    (defaultUnitOfMeasure?.toLowerCase() === UOM.metric ? UOM.metric : UOM.us);

  const jobRecommendationsAction = (ssId, isPM) => {
    const redirectUrl = isHeadlessBlocksSSOEnabled
      ? replaceTokensInString(
          headlessLinks.HEADLESS_SELF_SERVICE_OPTION_PAGE,
          esiteURL,
          salesModel,
          ssId
        )
      : `${replaceTokensInUrl(
          SSRO_BUNDLE_PAGE,
          storeId,
          catalogId,
          langId,
          ssId,
          isPM,
          serialNumber,
          salesModel,
          selectedUnit
        )}${LOCATION_HASH_KEY.MAINTENANCE_REPAIR}`;
    navigateToUrl(redirectUrl);
  };

  const handleClick = (jobRecommandationData, index) => {
    fireProductClickEvent({
      list: PCC_JOBS_RECOMMENDATIONS_LIST,
      products: [
        {
          businessUnit: 'PCC',
          id: jobRecommandationData.jobId,
          list: PCC_JOBS_RECOMMENDATIONS_LIST,
          name: jobRecommandationData.jobName,
          partNumber: jobRecommandationData.jobId,
          position: index + 1,
          relatedModel: salesModel
        }
      ],
      dealerStore
    });
    jobRecommendationsAction(
      jobRecommandationData.jobId,
      jobRecommandationData.isPM
    );
  };

  const findJobName = (arr, key) => {
    return arr?.find(obj => key in obj) !== undefined;
  };
  const showJobSection = findJobName(jobRecommandationData, 'jobName');

  useEffect(() => {
    gaJobsData?.length > 0 &&
      showJobSection &&
      fireProductImpressionEvent(PCC_JOBS_RECOMMENDATIONS_LIST, gaJobsData);
  }, [serialNumber, jobRecommandationData, showJobSection]);

  return (
    showJobSection && (
      <div className="mt-3" id="pdp-job-rec-list-container">
        <p className="text-uppercase text-univers--bold font-weight-bold mb-0">
          {t('JOBS_REC_PDP_HEADER')}
        </p>
        <p className="u-fs--14">{t('JOBS_REC_PDP_DESCRIPTION')}</p>
        {jobRecommandationData?.map(
          (item, index) =>
            item.jobName && (
              <div key={item.jobId}>
                <AlloyButton
                  buttonType="link"
                  size="dynamic"
                  className={`ps-0 pb-0 pt-0 text-start text-decoration-underline u-fs--16 ${styles['job-recommendations__list']}`}
                  onClick={() =>
                    handleClick(jobRecommandationData[index], index)
                  }
                >
                  {capitalizeText(item.jobName)}
                </AlloyButton>
              </div>
            )
        )}
      </div>
    )
  );
};

export default JobRecommendations;
